import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactPlayer from "react-player";
import {
  getFacebookEmbedIframeSrc,
  getFacebookVideoPageUrl,
  getInstagramEmbedSrc,
  getLoomEmbedSrc,
  getLoomPosterFallbacks,
  getPlayableVideoUrl,
  getFathomEmbedSrc,
  getReadAiMeetingId,
  withEmbedAutoplay,
} from "../../Utils/videoEmbedUrl";
import useHostedVideoPoster from "../../hooks/useHostedVideoPoster";
import {
  pauseHostedEmbedIframes,
  registerHostedEmbedRootClaim,
  unregisterHostedEmbedRootClaim,
  useRegisterSingleActiveMedia,
  useSingleActiveMedia,
} from "../../hooks/useSingleActiveMedia";
import "../../css/hosted-video-embed.css";

function EmbedEngageOverlay({
  posterUrl,
  posterFallbacks = [],
  label,
  onEngage,
}) {
  const fallbackIndexRef = useRef(0);
  const [imgSrc, setImgSrc] = useState(posterUrl || null);

  useEffect(() => {
    fallbackIndexRef.current = 0;
    setImgSrc(posterUrl || null);
  }, [posterUrl]);

  const handlePosterError = () => {
    const src = imgSrc || "";
    if (src.includes("maxresdefault")) {
      setImgSrc(src.replace("maxresdefault", "hqdefault"));
      return;
    }
    if (src.includes("hqdefault")) {
      setImgSrc(src.replace("hqdefault", "mqdefault"));
      return;
    }
    if (src.includes("mqdefault")) {
      setImgSrc(src.replace("mqdefault", "0"));
      return;
    }
    if (src.includes("cdn.loom.com") && src.endsWith(".jpg")) {
      setImgSrc(src.replace(/\.jpg$/, ".gif"));
      return;
    }
    const fallbacks = posterFallbacks.filter((u) => u && u !== src);
    const next = fallbacks[fallbackIndexRef.current];
    if (next) {
      fallbackIndexRef.current += 1;
      setImgSrc(next);
      return;
    }
    setImgSrc(null);
  };

  const displayPoster = imgSrc || posterUrl;

  return (
    <button
      type="button"
      className={`hosted-video-embed__engage${displayPoster ? " hosted-video-embed__engage--has-poster" : ""}`}
      style={
        displayPoster
          ? { backgroundImage: `url("${displayPoster}")` }
          : undefined
      }
      onClick={onEngage}
      aria-label={label}
    >
      {displayPoster ? (
        <img
          src={displayPoster}
          alt=""
          className="hosted-video-embed__engage-poster"
          onError={handlePosterError}
          loading="lazy"
          decoding="async"
        />
      ) : null}
      <span className="hosted-video-embed__engage-icon" aria-hidden="true">
        ▶
      </span>
    </button>
  );
}

export default function HostedVideoEmbed({ url }) {
  const trimmed = (url || "").trim();
  const loomEmbed = getLoomEmbedSrc(trimmed);
  const igEmbed = getInstagramEmbedSrc(trimmed);
  const fbPage = getFacebookVideoPageUrl(trimmed);
  const fathomEmbed = getFathomEmbedSrc(trimmed);
  const readAiId = getReadAiMeetingId(trimmed);

  const iframeSrc = loomEmbed || igEmbed || fathomEmbed;

  const ytVimeo = getPlayableVideoUrl(trimmed);
  const reactSrc =
    ytVimeo ||
    (!iframeSrc &&
    !fbPage &&
    !readAiId &&
    !/spotify\.com/i.test(trimmed) &&
    typeof ReactPlayer.canPlay === "function" &&
    ReactPlayer.canPlay(trimmed)
      ? trimmed
      : null);

  const { register, claimActive, releaseActive } = useSingleActiveMedia();
  const playerRootRef = useRef(null);
  const playerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [iframeEngaged, setIframeEngaged] = useState(false);
  const [loomMounted, setLoomMounted] = useState(false);
  const [reactEngaged, setReactEngaged] = useState(false);
  const [fbSession, setFbSession] = useState(0);
  const [iframeSession, setIframeSession] = useState(0);

  const claimEmbedActive = useCallback(() => {
    claimActive(null, playerRootRef.current);
  }, [claimActive]);

  const stopPlayback = useCallback(() => {
    pauseHostedEmbedIframes(playerRootRef.current);
    setPlaying(false);
    setIframeEngaged(false);
    setLoomMounted(false);
    setReactEngaged(false);
    setFbSession((s) => s + 1);
    setIframeSession((s) => s + 1);
    try {
      const internal = playerRef.current?.getInternalPlayer?.();
      internal?.pause?.();
    } catch {
      // ignore cross-origin / unsupported players
    }
  }, []);

  useRegisterSingleActiveMedia(stopPlayback, register);

  useEffect(() => {
    if (!fbPage) return undefined;
    const root = playerRootRef.current;
    if (!root) return undefined;
    registerHostedEmbedRootClaim(root, claimEmbedActive);
    return () => unregisterHostedEmbedRootClaim(root);
  }, [fbPage, claimEmbedActive, fbSession]);

  const posterUrl = useHostedVideoPoster(trimmed);
  const posterFallbacks = useMemo(
    () => (loomEmbed ? getLoomPosterFallbacks(trimmed) : []),
    [loomEmbed, trimmed],
  );

  const handleEngageIframe = () => {
    claimEmbedActive();
    setIframeEngaged(true);
  };

  const handleEngageLoom = () => {
    claimEmbedActive();
    setLoomMounted(true);
  };

  const handleEngageReactPlayer = () => {
    claimEmbedActive();
    setReactEngaged(true);
    setPlaying(true);
  };

  const handlePlay = () => {
    claimEmbedActive();
    setPlaying(true);
  };

  const handlePause = () => {
    setPlaying(false);
    releaseActive();
  };

  const fathomId = trimmed.match(
    /fathom\.video\/calls\/([a-zA-Z0-9_-]+)/i,
  )?.[1];

  if (readAiId || fathomId) {
    const isReadAi = !!readAiId;
    const title = isReadAi
      ? "Read.ai Meeting Recording"
      : "Fathom Meeting Recording";
    const description = isReadAi
      ? "View the meeting summary, transcript, and video recording on Read.ai."
      : "Watch the meeting recording and view the AI-generated summary on Fathom.";
    const domain = isReadAi ? "app.read.ai" : "fathom.video";
    const icon = isReadAi
      ? "https://app.read.ai/favicon.ico"
      : "https://fathom.video/favicon.ico";

    return (
      <div className="hosted-video-embed-block hosted-video-embed-block--card">
        <a
          href={trimmed}
          target="_blank"
          rel="noopener noreferrer"
          className="hosted-video-embed__external-card"
        >
          <div className="hosted-video-embed__card-content">
            <div className="hosted-video-embed__card-title">{title}</div>
            <div className="hosted-video-embed__card-description">
              {description}
            </div>
            <div className="hosted-video-embed__card-footer">
              <img
                src={icon}
                alt={domain}
                className="hosted-video-embed__card-icon"
              />
              <span className="hosted-video-embed__card-domain">{domain}</span>
            </div>
          </div>
          <div className="hosted-video-embed__card-arrow">↗</div>
        </a>
      </div>
    );
  }

  if (!fbPage && !iframeSrc && !reactSrc) return null;

  const iframeTitle = loomEmbed
    ? "Loom video"
    : fathomEmbed
      ? "Fathom recording"
      : "Instagram";

  return (
    <div
      className={`hosted-video-embed${fbPage ? " hosted-video-embed--facebook" : ""}`}
    >
      <div
        className={`hosted-video-embed__frame${fbPage ? " hosted-video-embed__frame--facebook" : ""}${loomEmbed ? " hosted-video-embed__frame--loom" : ""}${posterUrl && !fbPage ? " hosted-video-embed__frame--has-poster" : ""}`}
      >
        <div
          ref={playerRootRef}
          className="hosted-video-embed__player-root"
          onPointerDown={() => {
            if (fbPage || iframeEngaged || loomMounted || reactEngaged) {
              claimEmbedActive();
            }
          }}
        >
          {fbPage ? (
            <div className="hosted-video-embed__fb-preview">
              <iframe
                key={`fb-${trimmed}-${fbSession}`}
                title="Facebook video"
                src={getFacebookEmbedIframeSrc(fbPage, { autoplay: false })}
                className="hosted-video-embed__fb-iframe"
                allow="autoplay; encrypted-media; fullscreen; clipboard-write; web-share"
              />
            </div>
          ) : loomEmbed ? (
            loomMounted ? (
              <iframe
                key={`loom-${trimmed}-${iframeSession}`}
                title="Loom video"
                src={withEmbedAutoplay(loomEmbed)}
                allow="autoplay; fullscreen; clipboard-read; clipboard-write; web-share"
                style={{ border: 0, width: "100%", height: "100%" }}
              />
            ) : (
              <EmbedEngageOverlay
                posterUrl={posterUrl}
                posterFallbacks={posterFallbacks}
                label="Play Loom video"
                onEngage={handleEngageLoom}
              />
            )
          ) : iframeSrc ? (
            iframeEngaged ? (
              <iframe
                key={`iframe-${trimmed}-${iframeSession}`}
                title={iframeTitle}
                src={withEmbedAutoplay(iframeSrc)}
                allow="autoplay; fullscreen; clipboard-read; clipboard-write; web-share"
                style={{ border: 0, width: "100%", height: "100%" }}
              />
            ) : (
              <EmbedEngageOverlay
                posterUrl={posterUrl}
                posterFallbacks={posterFallbacks}
                label={`Play ${iframeTitle}`}
                onEngage={handleEngageIframe}
              />
            )
          ) : reactEngaged ? (
            <div className="hosted-video-embed__react-player-wrap">
              <ReactPlayer
                ref={playerRef}
                src={reactSrc}
                width="100%"
                height="100%"
                playing={playing}
                controls
                playsInline
                style={{ display: "block" }}
                onPlay={handlePlay}
                onPause={handlePause}
                onEnded={handlePause}
                config={{
                  youtube: {
                    playerVars: { modestbranding: 1, rel: 0, autoplay: 1 },
                  },
                }}
              />
            </div>
          ) : (
            <EmbedEngageOverlay
              posterUrl={posterUrl}
              posterFallbacks={posterFallbacks}
              label="Play video"
              onEngage={handleEngageReactPlayer}
            />
          )}
        </div>
      </div>
    </div>
  );
}
